import { useNavigate } from "react-router-dom"
import clsx from "clsx"
import { EditIcon } from "../../../ui/icons/edit"
import { formatterDate, optionsFormatter, ReportingTypologyOption } from "../../../utils"
import { useEffect, useState } from "react"
import { useAppDispatch, useAppSelector } from "../../../lib/redux/hooks"
import { GetAllRID, GetRIDBYId, resetGetAllRIDStatus, resetRIDDeleteStatus, RIDDelete, setRIDIdToUpdate, setUpdateRIDAttachmentIds, setUpdateRIDCertificateRequestProtocolId, setUpdateRIDClosedNote, setUpdateRIDClosingProtocolIds, setUpdateRIDDeadlineRequestedDocument, setUpdateRIDDepartureProtocolId, setUpdateRIDDocumentalControlProtocolId, setUpdateRIDDocumentNotes, setUpdateRIDNewMeasureId, setUpdateRIDNote, setUpdateRIDObject, setUpdateRIDOldMeasureId, setUpdateRIDOther, setUpdateRIDReportingId } from "./slice"
import { RequestStateDTO, RIDEntityDTO } from "./dataService/dto"
import { TrashIcon } from "../../../ui/icons/trash"
import { Alert } from "../../../ui/organisms/alert"
import Button from "../../../ui/molecules/button"

export function RIDList() {
    const navigate = useNavigate()
    const dispatch = useAppDispatch()
    const ridState = useAppSelector(state => state.rid)
    const sessionState = useAppSelector(state => state.session)
    const userState = useAppSelector(state => state.users)
    const [showAlert, setShowAlert] = useState<boolean>(false)
    const [ridToDelete, setRidToDelete] = useState<string | null>(null)

    useEffect(() => {
        dispatch(GetAllRID({ itemsPerPage: 0, page: 0, customerId: sessionState.selected.customerId! }))
    }, [dispatch, sessionState.selected.customerId])

    useEffect(() => {
        if (ridState.getAllRidStatus === 'successfully' &&
            ridState.getAllRidResponse !== undefined
        ) {
            dispatch(resetGetAllRIDStatus())
        }
    }, [dispatch, ridState.getAllRidStatus, ridState.getAllRidResponse])

    useEffect(() => {
        if (ridState.ridDeleteStatus === 'successfully') {
            dispatch(resetRIDDeleteStatus())
            dispatch(GetAllRID({ itemsPerPage: 0, page: 0, customerId: sessionState.selected.customerId! }))
        }
    }, [dispatch, ridState.ridDeleteStatus, sessionState.selected.customerId])

    const handleOnEdit = (rid: RIDEntityDTO) => {
        dispatch(setRIDIdToUpdate(rid.id))
        dispatch(setUpdateRIDDepartureProtocolId(rid.departureProtocolId))
        dispatch(setUpdateRIDObject(rid.object))
        dispatch(setUpdateRIDNote(rid.note))
        dispatch(setUpdateRIDDeadlineRequestedDocument(rid.deadlineRequestedDocument))
        dispatch(setUpdateRIDDocumentalControlProtocolId(rid.documentalControlProtocolId))
        dispatch(setUpdateRIDCertificateRequestProtocolId(rid.certificateRequestProtocolId))
        dispatch(setUpdateRIDOther(rid.other))
        dispatch(setUpdateRIDReportingId(rid.reportingId))
        dispatch(setUpdateRIDAttachmentIds(rid.attachmentIds))
        dispatch(setUpdateRIDClosingProtocolIds(rid.closingProtocolIds))
        dispatch(setUpdateRIDClosedNote(rid.closedNote))
        dispatch(setUpdateRIDDocumentNotes(rid.documentNotes))
        dispatch(setUpdateRIDNewMeasureId(rid.newMeasureId))
        dispatch(setUpdateRIDOldMeasureId(rid.oldMeasureId))

        dispatch(GetRIDBYId(rid.id))

        navigate('/editRID')
    }

    const showMotivations = (rid: RIDEntityDTO) => {
        const motivations: string[] = []
        if (rid.documentalControlProtocolInfo !== null)
            motivations.push(`Controllo documentale: ${rid.documentalControlProtocolInfo}`)
        if (rid.certificateRequestProtocolInfo !== null)
            motivations.push(`Richiesta certificato: ${rid.certificateRequestProtocolInfo}`)
        if (rid.reportingInfo !== null)
            motivations.push(`Segnalazione: ${optionsFormatter(rid.reportingInfo, ReportingTypologyOption)}`)
        if (rid.other !== null)
            motivations.push(`Altro: ${rid.other}`)

        return (
            <ul className="list-disc list-inside space-y-1">{motivations.map((m, index) => (<li key={`${rid.id}-mot-${index}`}>{m}</li>))}</ul>
        )
    }

    return (
        <div>
            <div className="w-full overflow-x-auto rounded-xl border border-neutral-200 shadow-sm">
                <table id="users-table" className="w-full border-collapse text-md">
                    <thead>
                        <tr className="bg-brandPrimary-600 text-white uppercase text-xs tracking-wide h-[52px]">
                            <th className="px-3 py-3 text-left font-semibold whitespace-nowrap rounded-tl-xl">Data</th>
                            <th className="px-3 py-3 text-left font-semibold whitespace-nowrap">Protocollo partenza</th>
                            <th className="px-3 py-3 text-left font-semibold whitespace-nowrap">Motivazione</th>
                            <th className="px-3 py-3 text-left font-semibold whitespace-nowrap">Stato</th>
                            <th className="px-3 py-3 text-center font-semibold whitespace-nowrap rounded-tr-xl w-[60px]"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {
                            ridState.getAllRidResponse?.data?.length ? (
                                ridState.getAllRidResponse.data.map((rid, index) =>
                                    <tr
                                        key={rid.id || index}
                                        className={`text-left border-b border-neutral-200 transition-colors duration-150 hover:bg-brandPrimary-50 ${index % 2 === 0 ? 'bg-white' : 'bg-neutral-50'}`}
                                    >
                                        <td className="px-3 py-2 text-neutral-700 whitespace-nowrap">{formatterDate(rid.date)}</td>
                                        <td className="px-3 py-2 text-neutral-700 whitespace-nowrap">{rid.departureProtocolInfo}</td>
                                        <td className="px-3 py-2 text-neutral-700 w-[30%]">{showMotivations(rid)}</td>
                                        <td className={clsx("px-3 py-2 w-[30%] font-semibold", rid.requestState === 'APERTA' ? "text-[#FF941A]" : "text-[#26AB45]")}>{rid.requestState}</td>
                                        <td className="px-3 py-2 w-[60px]">
                                            <div className="flex items-center justify-center gap-1.5">
                                                <button className="relative group p-1 rounded hover:bg-neutral-100 transition-colors" onClick={() => handleOnEdit(rid)}>
                                                    <EditIcon color="orange" size={20} />
                                                    <span className="absolute bottom-full right-0 mb-1 hidden group-hover:block px-2 py-1 text-xs text-white bg-neutral-800 rounded whitespace-nowrap pointer-events-none z-10">
                                                        Modifica R.I.D.
                                                    </span>
                                                </button>
                                                {
                                                    userState.findMeResponse.role === 'admin' ? (
                                                        <button className="relative group p-1 rounded hover:bg-neutral-100 transition-colors" onClick={() => {
                                                            setRidToDelete(rid.id)
                                                            setShowAlert(true)
                                                        }}>
                                                            <TrashIcon color="red" size={20} />
                                                            <span className="absolute bottom-full right-0 mb-1 hidden group-hover:block px-2 py-1 text-xs text-white bg-neutral-800 rounded whitespace-nowrap pointer-events-none z-10">
                                                                Elimina R.I.D.
                                                            </span>
                                                        </button>
                                                    ) : rid.requestState === RequestStateDTO.APERTA ? (
                                                        <button className="relative group p-1 rounded hover:bg-neutral-100 transition-colors" onClick={() => {
                                                            setRidToDelete(rid.id)
                                                            setShowAlert(true)
                                                        }}>
                                                            <TrashIcon color="red" size={20} />
                                                            <span className="absolute bottom-full right-0 mb-1 hidden group-hover:block px-2 py-1 text-xs text-white bg-neutral-800 rounded whitespace-nowrap pointer-events-none z-10">
                                                                Elimina R.I.D.
                                                            </span>
                                                        </button>
                                                    ) : null
                                                }
                                            </div>
                                        </td>
                                    </tr>
                                )
                            ) : (
                                <tr>
                                    <td colSpan={5} className="px-3 py-3 text-center text-neutral-500 bg-white">
                                        Nessun R.I.D. trovato
                                    </td>
                                </tr>
                            )
                        }
                    </tbody>
                </table>
            </div>
            {
                (showAlert && ridToDelete) && (
                    <Alert title="Conferma operazione" close={() => setShowAlert(false)}>
                        <div className="flex flex-col justify-center">
                            <div className="pb-3">
                                <div>
                                    Sei sicuro di voler eliminare il R.I.D.?
                                </div>
                            </div>
                            <div className="mt-6 flex items-center justify-center gap-8">
                                <Button
                                    variant="outline"
                                    color="dimgrey"
                                    iconPosition="off"
                                    label="Annulla"
                                    size="md"
                                    onClick={() => setShowAlert(false)}
                                />
                                <Button
                                    variant="solid"
                                    color="orange"
                                    iconPosition="off"
                                    label="Elimina"
                                    size="md"
                                    onClick={() => {
                                        dispatch(RIDDelete(ridToDelete))
                                        setShowAlert(false)
                                    }}
                                />
                            </div>

                        </div>
                    </Alert>
                )
            }
        </div>
    )
}